// src/components/learning/prof/LettreInteractive.js
// Version complète — ISO avec les 24 slides du prof
// 11 écrans correspondant exactement à la progression pédagogique

import React, { useState, useEffect, useRef } from 'react';
import { LetterTracingAnimation, EcritureCanvas } from './EcritureCanvas';
import LETTRES_DATA_RAW from './lettres_data';
import LETTRES_UPDATES from './lettres_updates.json';

// ─────────────────────────────────────────────
// COULEURS
// ─────────────────────────────────────────────
const C = {
  green: '#065f46',
  greenLight: '#E1F5EE',
  greenMid: '#1D9E75',
  greenBorder: '#a7f3d0',
  text: '#1a1a1a',
  muted: '#6b7280',
  white: '#fff',
  bg: '#f8f7f4',
  yellow: '#f59e0b',
  yellowLight: '#fffbeb',
  red: '#E24B4A',
  redLight: '#FCEBEB',
  blue: '#3b82f6',
  blueLight: '#eff6ff',
};

// ─────────────────────────────────────────────
// ADAPTATEUR — Convertit lettres_data.js vers le format LettreInteractive
// ─────────────────────────────────────────────

// Parser le format <HL>texte</HL> en {avant, highlight, apres}
const parseHighlight = (str) => {
  if (!str) return { avant: '', highlight: '', apres: '' };
  const startTag = '<HL>';
  const endTag = '</HL>';
  const i1 = str.indexOf(startTag);
  const i2 = str.indexOf(endTag);
  if (i1 === -1 || i2 === -1) return { avant: str, highlight: '', apres: '' };
  return {
    avant: str.slice(0, i1),
    highlight: str.slice(i1 + startTag.length, i2),
    apres: str.slice(i2 + endTag.length),
  };
};

// Construire index par lettre arabe
const LETTRES_INDEX = {};
Object.values(LETTRES_DATA_RAW).forEach(l => {
  if (l.lettre) LETTRES_INDEX[l.lettre] = l;
});

// Récupérer les updates pour une lettre (par clé ou par caractère)
const getUpdates = (raw) => {
  if (!raw) return {};
  // Chercher dans updates par nom de clé (ex: 'ghayn', 'fa', etc.)
  const keyMap = {
    'غ': 'ghayn', 'ف': 'fa', 'م': 'mim', 'و': 'waw',
    'ع': 'ayn', 'ك': 'kaf', 'ل': 'lam', 'ن': 'nun',
    'ب': 'ba',
  };
  const key = keyMap[raw.lettre];
  return key && LETTRES_UPDATES[key] ? LETTRES_UPDATES[key] : {};
};

// Adapter les données brutes au format attendu par LettreInteractive
const adaptLettre = (raw) => {
  const updates = getUpdates(raw);
  if (!raw) return null;
  return {
    lettre: raw.lettre,
    nom: raw.nom,
    transliteration: raw.transliteration,
    niveau: raw.niveau || 'Abjad',
    ordre: raw.ordre,
    objectifs: [
      'التعرف السمعي للصوت معزولاً ومع جميع الحركات والتنوين',
      'التحقيق النطقي للصوت معزولاً ومع جميع الحركات والتنوين',
      'تحقيق التطابق الصوتي الخطي للصوت معزولاً مع جميع الحركات والتنوين',
      'قراءة مقاطع وكلمات وجمل تتضمن الحرف',
      'التحقيق الخطي للحرف معزولاً وضمن كلمات',
    ],
    objectifs_fr: [
      'Reconnaître le son seul et avec toutes les voyelles',
      'Prononcer correctement le son',
      'Associer le son écrit à la lettre',
      'Lire des syllabes, mots et phrases contenant la lettre',
      'Écrire la lettre seule et dans des mots',
    ],
    situation_depart: {
      personnage: (updates.situation_depart || raw.situation_depart)?.personnage || '',
      emoji: (updates.situation_depart || raw.situation_depart)?.emoji || '📖',
      texte: (updates.situation_depart || raw.situation_depart)?.texte || '',
      texte_fr: '',
      question: (updates.situation_depart || raw.situation_depart)?.question || '',
      question_fr: '',
    },
    articulation: raw.articulation || '',
    articulation_fr: '',
    formes: (raw.formes || []).map(f => ({
      forme: f.forme,
      position: f.position,
      position_ar: f.position_ar,
      exemple: '',
    })),
    prenoms: updates.prenoms || [],
    mots_conscience: (updates.mots_exemples || raw.mots_exemples || []).slice(0, 4).map(m => {
      const { avant, highlight, apres } = parseHighlight(m.mot_highlight);
      return {
        mot: m.mot,
        highlight_avant: avant,
        highlight: highlight,
        highlight_apres: apres,
        fr: m.fr,
        emoji: m.emoji || '📖',
      };
    }),
    position_son: (raw.mots_exemples || []).slice(0, 4).map(m => {
      const { avant, highlight, apres } = parseHighlight(m.mot_highlight);
      return {
        mot: m.mot,
        highlight_avant: avant,
        highlight: highlight,
        highlight_apres: apres,
        fr: m.fr,
        emoji: m.emoji || '📖',
        position: m.position || 'milieu',
      };
    }),
    harakat_courtes: (raw.harakat_courtes || []).map((s, i) => ({
      syllabe: s,
      nom: ['فتحة', 'كسرة', 'ضمة', 'سكون'][i] || '',
      nom_fr: ['fatha', 'kasra', 'damma', 'sukun'][i] || '',
    })),
    harakat_longues: (raw.harakat_longues || []).map((s, i) => {
      // Nettoyer les valeurs avec description textuelle (ex: "ـا (voyelle longue)")
      const syllabe = s.includes('(') ? s.split('(')[0].trim() : s;
      return {
        syllabe: syllabe,
        nom: ['ألف المد', 'واو المد', 'ياء المد'][i] || '',
        nom_fr: ['alif', 'waw', 'ya'][i] || '',
      };
    }),
    decomposition: raw.decomposition_syllabique ? [raw.decomposition_syllabique] : [],
    decomposition_consigne: 'أبحث عن كلمات تتضمن هذا الحرف وأجزئها إلى مقاطع',
    fluidite: {
      niveau1_syllabes: raw.harakat_courtes || [],
      niveau2_mots: updates.fluidite_mots || (updates.mots_exemples || raw.mots_exemples || []).map(m => m.mot),
      niveau3_phrases: (updates.phrases || raw.phrases || []).map(p => ({ ar: p.ar, fr: p.fr })),
    },
    ecriture: updates.ecriture || [
      { ligne: raw.lettre + 'ٌ', type: 'lettre', label: 'La lettre avec tanwin' },
      { ligne: raw.lettre + 'َـ', type: 'lettre', label: 'La lettre avec fatha' },
      ...(raw.exercice_ecriture ? [
        { ligne: raw.exercice_ecriture.phrase?.split(' ')[0] || raw.lettre, type: 'mot', label: 'Un mot' },
        { ligne: raw.exercice_ecriture.phrase || '', type: 'phrase', label: raw.exercice_ecriture.fr || 'Une phrase complète' },
      ] : []),
    ],
  };
};

// Données ي complètes (ISO slides prof — garde la version détaillée)
// ─────────────────────────────────────────────
// DONNÉES LETTRE ي — ISO avec slides prof
// ─────────────────────────────────────────────
const DATA_YA = {
  lettre: 'ي',
  nom: 'الياء',
  transliteration: 'Ya',
  niveau: 'Abjad',
  ordre: 28,

  // Slide 2 — Objectifs
  objectifs: [
    'التعرف السمعي للصوت [ي] معزولاً ومع جميع الحركات والتنوين',
    'التحقيق النطقي للصوت [ي] معزولاً ومع جميع الحركات والتنوين',
    'تحقيق التطابق الصوتي الخطي للصوت معزولاً مع جميع الحركات والتنوين',
    'قراءة مقاطع وكلمات وجمل تتضمن الحرف',
    'التحقيق الخطي للحرف معزولاً وضمن كلمات',
  ],
  objectifs_fr: [
    'Reconnaître le son [ي] seul et avec toutes les voyelles',
    'Prononcer correctement le son [ي]',
    'Associer le son écrit à la lettre',
    'Lire des syllabes, mots et phrases contenant la lettre',
    'Écrire la lettre seule et dans des mots',
  ],

  // Slide 3 — Situation de départ
  situation_depart: {
    personnage: 'أيْمَنُ',
    emoji: '🚿',
    texte: 'يَغْسِلُ أيْمَنٌ يَدَيْهِ في الْمِغْسَلِ',
    texte_fr: 'Ayman se lave les mains dans le lavabo',
    question: 'ماذا يَفْعَلُ أيْمَنُ؟',
    question_fr: 'Que fait Ayman ?',
  },

  // Slides 4-5 — Lettre + articulation
  articulation: 'يخرج الصوت [ي] من وسط اللسان مع ما يقابله من وسط الحنك الأعلى',
  articulation_fr: 'Le son [ي] sort du milieu de la langue avec le palais supérieur',

  // Slide 6 — Les 4 formes
  formes: [
    { forme: 'يـ', position: 'début', position_ar: 'أول الكلمة', exemple: 'يَدٌ' },
    { forme: 'ـيـ', position: 'milieu', position_ar: 'وسط الكلمة', exemple: 'خَيْمَةٌ' },
    { forme: 'ـي', position: 'fin', position_ar: 'آخر الكلمة', exemple: 'كُرْسِيٌّ' },
    { forme: 'ي', position: 'isolée', position_ar: 'معزولة', exemple: 'ي' },
  ],

  // Slide 7 — Lecture prénoms
  prenoms: [
    { ar: 'أيْمَنُ', highlight_avant: '', highlight: 'يْ', highlight_apres: 'مَنُ', fr: 'Ayman' },
    { ar: 'مَرْيَمُ', highlight_avant: 'مَرْ', highlight: 'يَ', highlight_apres: 'مُ', fr: 'Maryam' },
  ],

  // Slides 8-15 — Conscience phonique
  mots_conscience: [
    { mot: 'خَيْمَةٌ', highlight_avant: 'خَ', highlight: 'يْ', highlight_apres: 'مَةٌ', fr: 'tente', emoji: '⛺' },
    { mot: 'لَيْمونٌ', highlight_avant: 'لَ', highlight: 'يْ', highlight_apres: 'مونٌ', fr: 'citron', emoji: '🍋' },
    { mot: 'كُرْسِيٌّ', highlight_avant: 'كُرْسِ', highlight: 'يٌّ', highlight_apres: '', fr: 'chaise', emoji: '🪑' },
    { mot: 'دُمْيَةٌ', highlight_avant: 'دُمْ', highlight: 'يَ', highlight_apres: 'ةٌ', fr: 'poupée', emoji: '🪆' },
  ],

  // Slides 16-17 — Position du son
  position_son: [
    { mot: 'يَدٌ', highlight_avant: '', highlight: 'يَ', highlight_apres: 'دٌ', fr: 'main', emoji: '🤚', position: 'début' },
    { mot: 'طُيورٌ', highlight_avant: 'طُ', highlight: 'يو', highlight_apres: 'رٌ', fr: 'oiseaux', emoji: '🐦', position: 'milieu' },
    { mot: 'شُرْطِيٌّ', highlight_avant: 'شُرْطِ', highlight: 'يٌّ', highlight_apres: '', fr: 'policier', emoji: '👮', position: 'fin' },
    { mot: 'نَايٌ', highlight_avant: 'نَا', highlight: 'يٌ', highlight_apres: '', fr: 'flûte', emoji: '🎵', position: 'fin' },
  ],

  // Slides 18-20 — Voyelles
  harakat_courtes: [
    { syllabe: 'يَ', nom: 'فتحة', nom_fr: 'fatha' },
    { syllabe: 'يِ', nom: 'كسرة', nom_fr: 'kasra' },
    { syllabe: 'يُ', nom: 'ضمة', nom_fr: 'damma' },
    { syllabe: 'يْ', nom: 'سكون', nom_fr: 'sukun' },
    { syllabe: 'يٌّ', nom: 'تنوين ضم', nom_fr: 'tanwin damm' },
    { syllabe: 'يٍّ', nom: 'تنوين كسر', nom_fr: 'tanwin kasr' },
    { syllabe: 'يًّا', nom: 'تنوين فتح', nom_fr: 'tanwin fath' },
  ],
  harakat_longues: [
    { syllabe: 'يا', nom: 'ألف المد', nom_fr: 'alif' },
    { syllabe: 'يو', nom: 'واو المد', nom_fr: 'waw' },
    { syllabe: 'يي', nom: 'ياء المد', nom_fr: 'ya' },
  ],

  // Slide 21 — Décomposition syllabique
  decomposition: [
    {
      mot: 'يَنْبوعٌ',
      fr: 'source',
      syllabes: ['يَنْـ', 'ـبو', 'عٌ'],
      nb: 3,
    },
    {
      mot: 'جَدْيٌ',
      fr: 'chevreau',
      syllabes: ['جَدْ', 'يٌ'],
      nb: 2,
    },
    {
      mot: 'خِيامٌ',
      fr: 'tentes',
      syllabes: ['خِـ', 'ـيا', 'مٌ'],
      nb: 3,
    },
  ],
  decomposition_consigne: 'أبحث عن كلمات تتضمن حرف الياء وأجزئها إلى مقاطع',

  // Slide 22 — Fluidité (3 niveaux)
  fluidite: {
    niveau1_syllabes: ['يِ', 'يَ', 'يُ', 'يا', 'يو', 'يي', 'يٌ', 'يٍ', 'ياً'],
    niveau2_mots: ['بُيوتٌ', 'عِيادَةٌ', 'شُرْطِيٌّ', 'خُيولٌ', 'رِياحٌ', 'عُيونٌ', 'صَبِيٌّ', 'مُحامِياً'],
    niveau3_phrases: [
      { ar: 'يُمْسِكُ يوسُفُ نايًا', fr: 'Youssef tient une flûte' },
      { ar: 'يَغـْسِلُ ياسينُ يَدَيْهِ إِلى الرُّسْغَـيْنِ', fr: 'Yassine se lave les mains jusqu\'aux poignets' },
      { ar: 'يُطْعِمُ أيْمَنُ طُيورًا', fr: 'Ayman nourrit des oiseaux' },
    ],
  },

  // Slide 23 — Écriture (4 lignes progressives)
  ecriture: [
    { ligne: 'يٌ', type: 'lettre', label: 'La lettre avec tanwin' },
    { ligne: 'يَـ', type: 'lettre', label: 'La lettre avec fatha' },
    { ligne: 'خَيّاطٌ', type: 'mot', label: 'Un mot — خَيّاطٌ (tailleur)' },
    { ligne: 'يَخيطُ الْخَيّاطُ بِالْمِخْيَطِ', type: 'phrase', label: 'Une phrase complète' },
  ],
};

// ─────────────────────────────────────────────
// COMPOSANTS UTILITAIRES
// ─────────────────────────────────────────────
const PlayBtn = ({ onClick, small = false }) => (
  <button onClick={onClick} style={{
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: small ? '7px 16px' : '11px 24px',
    background: `linear-gradient(135deg, ${C.green}, #047857)`,
    color: '#fff', border: 'none',
    borderRadius: 40, fontSize: small ? 12 : 14,
    fontWeight: 600, cursor: 'pointer',
    transition: 'all 0.2s',
  }}>
    <svg width={small ? 12 : 15} height={small ? 12 : 15} viewBox="0 0 24 24" fill="#fff">
      <path d="M8 5v14l11-7z"/>
    </svg>
    Écouter
  </button>
);

const Tag = ({ children, color = C.green, bg = C.greenLight }) => (
  <span style={{
    background: bg, color, fontSize: 11,
    padding: '3px 10px', borderRadius: 20, fontWeight: 600,
  }}>{children}</span>
);

const MotArabe = ({ avant, highlight, apres, size = 22 }) => (
  <span style={{ fontSize: size, direction: 'rtl', fontFamily: "'Amiri', serif" }}>
    <span style={{ color: C.text }}>{avant}</span>
    <span style={{ color: C.green, fontWeight: 700 }}>{highlight}</span>
    <span style={{ color: C.text }}>{apres}</span>
  </span>
);

// Animation lettre
const LetterHero = ({ letter, size = 100 }) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => { setTimeout(() => setVisible(true), 200); }, []);
  return (
    <div style={{
      fontSize: size, lineHeight: 1, color: C.green,
      fontFamily: "'Amiri', serif", direction: 'rtl',
      transition: 'all 0.5s cubic-bezier(.34,1.56,.64,1)',
      opacity: visible ? 1 : 0,
      transform: visible ? 'scale(1)' : 'scale(0.5)',
      filter: `drop-shadow(0 6px 20px ${C.green}44)`,
      userSelect: 'none',
    }}>
      {letter}
    </div>
  );
};

// Lookup des données
const getLetterData = (letter) => {
  if (letter === 'ي') return DATA_YA;
  const raw = LETTRES_INDEX[letter];
  if (raw) return adaptLettre(raw);
  return DATA_YA; // fallback
};

// ─────────────────────────────────────────────
// COMPOSANT PRINCIPAL
// ─────────────────────────────────────────────
const LettreInteractive = ({ letterChar = 'ي', onClose, onProgressUpdate, client, userType = 'prof', initialScreen = 0, isSlaveMode = false, token = null }) => {
  const data = getLetterData(letterChar);
  const TOTAL = 11;

  const [screen, setScreen] = useState(initialScreen);
  const [done, setDone] = useState(new Set());

  // Enregistrer une interaction côté élève uniquement
  const trackProgression = async (action) => {
    if (userType === 'prof' || !token || !letterChar) return;
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/progression/lettre`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ lettre: letterChar, niveau: 'Abjad', action })
      });
    } catch (e) {
      console.error('Erreur track progression:', e);
    }
  };

  // Broadcaster l'ouverture dès que le prof ouvre le support
  // + tracker lettre_vue côté élève
  useEffect(() => {
    if (userType === 'prof' && client) {
      broadcastScreen(initialScreen);
    }
    if (userType !== 'prof') {
      trackProgression('lettre_vue');
    }
    // À la fermeture, broadcaster lettre_interactive_close
    return () => {
      if (userType === 'prof' && client) {
        client.sendStreamMessage(
          new TextEncoder().encode(JSON.stringify({ type: 'lettre_interactive_close' }))
        ).catch(() => {});
      }
    };
  }, []);
  const [playingWord, setPlayingWord] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);
  const [thumbUp, setThumbUp] = useState(null);
  const [positionSelected, setPositionSelected] = useState(null);
  const [fluiditeLevel, setFluiditeLevel] = useState(1);
  const [ecritureStep, setEcritureStep] = useState(0);

  const playAudio = (text) => {
    setPlayingWord(text);
    setTimeout(() => setPlayingWord(null), 1000);
    // TODO: brancher ElevenLabs
    // fetch(`/api/tts?text=${encodeURIComponent(text)}&lang=ar`)
  };

  // Broadcaster le changement d'écran vers l'élève
  const broadcastScreen = async (newScreen) => {
    if (!client || userType !== 'prof') return;
    try {
      const msg = {
        type: 'lettre_interactive_sync',
        letterChar,
        screen: newScreen,
      };
      await client.sendStreamMessage(new TextEncoder().encode(JSON.stringify(msg)));
    } catch (e) {
      console.error('Erreur broadcast écran:', e);
    }
  };

  // Écouter la sync côté élève
  useEffect(() => {
    if (userType === 'prof') return;
    const handler = (event) => {
      const msg = event.detail;
      if (msg.type === 'lettre_interactive_sync' && msg.letterChar === letterChar) {
        setScreen(msg.screen);
        setSelectedForm(null);
        setThumbUp(null);
        setFluiditeLevel(1);
      }
    };
    window.addEventListener('agora-document-message', handler);
    return () => window.removeEventListener('agora-document-message', handler);
  }, [userType, letterChar]);

  const navigate = (dir) => {
    if (isSlaveMode) return; // Élève ne peut pas naviguer
    const next = screen + dir;
    if (next < 0 || next >= TOTAL) return;
    setDone(prev => new Set([...prev, screen]));
    setScreen(next);
    setSelectedForm(null);
    setThumbUp(null);
    setPositionSelected(null);
    setFluiditeLevel(1);
    setEcritureStep(0);
    broadcastScreen(next);
  };

  // Tracker côté élève selon l'écran reçu via Agora
  useEffect(() => {
    if (userType === 'prof') return;
    if (screen === 5) trackProgression('conscience_phonique');
    if (screen === 10) trackProgression('ecriture_tentee');
  }, [screen]);

  const screenTitles = [
    'الأهداف — Objectifs',
    'وضعية الانطلاق — Mise en situation',
    'حرف الياء — La lettre',
    'أشكال الحرف — Les 4 formes',
    'اقرأ — Lecture de prénoms',
    'الوعي الصوتي — Conscience phonique',
    'موقع الصوت — Position du son',
    'الحركات — Voyelles',
    'التجزيء المقطعي — Syllabes',
    'الطلاقة — Fluidité',
    'أتدرب وأكتب — Écriture',
  ];

  // ──────────────────────────────────────────
  // ÉCRANS
  // ──────────────────────────────────────────
  const renderScreen = () => {
    switch (screen) {

      // ── ÉCRAN 0 : Objectifs (Slide 2) ──────
      case 0: return (
        <div style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <Tag>مادة : القراءة</Tag>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {(data.objectifs || []).map((obj, i) => (
              <div key={i} style={{
                background: C.white, border: `0.5px solid #e5e7eb`,
                borderRadius: 12, padding: '14px 16px',
                display: 'flex', gap: 12, alignItems: 'flex-start',
              }}>
                <div style={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: C.greenLight, color: C.green,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 700, flexShrink: 0,
                }}>
                  {i + 1}
                </div>
                <div>
                  <div style={{ fontSize: 17, direction: 'rtl', color: C.text, marginBottom: 4, fontFamily: "'Amiri', serif", lineHeight: 1.6 }}>
                    {obj}
                  </div>
                  <div style={{ fontSize: 12, color: C.muted, fontStyle: 'italic' }}>
                    {data.objectifs_fr[i]}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );

      // ── ÉCRAN 1 : Situation de départ (Slide 3) ──
      case 1: return (
        <div style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <Tag>وضعية الانطلاق</Tag>
          </div>
          <div style={{
            background: C.greenLight, border: `1.5px solid ${C.greenBorder}`,
            borderRadius: 16, padding: 28, textAlign: 'center', marginBottom: 16,
          }}>
            <div style={{ fontSize: 130, marginBottom: 20 }}>{data.situation_depart.emoji}</div>
            <div style={{
              fontSize: 56, direction: 'rtl', color: C.text,
              lineHeight: 1.8, marginBottom: 8, fontFamily: "'Amiri', serif",
            }}>
              {data.situation_depart.texte}
            </div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 20 }}>
              {data.situation_depart.texte_fr}
            </div>
            <PlayBtn onClick={() => playAudio(data.situation_depart.texte)} />
          </div>
          <div style={{
            background: C.white, border: `0.5px solid #e5e7eb`,
            borderRadius: 12, padding: '14px 20px',
          }}>
            <div style={{ fontSize: 14, color: C.muted, marginBottom: 6 }}>السؤال</div>
            <div style={{ fontSize: 18, direction: 'rtl', color: C.text, fontFamily: "'Amiri', serif", marginBottom: 4 }}>
              {data.situation_depart.question}
            </div>
            <div style={{ fontSize: 13, color: C.muted, fontStyle: 'italic' }}>
              {data.situation_depart.question_fr}
            </div>
          </div>
        </div>
      );

      // ── ÉCRAN 2 : Lettre + Articulation (Slides 4-5) ──
      case 2: return (
        <div style={{ textAlign: 'center' }}>
          <LetterHero letter={data.lettre} />
          <div style={{ fontSize: 22, color: C.text, fontWeight: 700, marginTop: 8, marginBottom: 4 }}>
            {data.nom}
          </div>
          <div style={{ fontSize: 14, color: C.muted, marginBottom: 20 }}>
            {data.transliteration} · {data.ordre}ème lettre de l'alphabet
          </div>
          <PlayBtn onClick={() => playAudio(data.lettre)} />
          <div style={{
            background: C.white, border: `0.5px solid #e5e7eb`,
            borderRadius: 12, padding: '16px 20px', marginTop: 20, textAlign: 'right',
          }}>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 6, textAlign: 'left' }}>
              التحقيق النطقي للصوت
            </div>
            <div style={{
              fontSize: 15, direction: 'rtl', color: C.text,
              fontFamily: "'Amiri', serif", lineHeight: 1.8, marginBottom: 8,
            }}>
              {data.articulation}
            </div>
            <div style={{ fontSize: 13, color: C.muted, fontStyle: 'italic', textAlign: 'left' }}>
              {data.articulation_fr}
            </div>
          </div>
        </div>
      );

      // ── ÉCRAN 3 : Les 4 formes (Slide 6) ──
      case 3: return (
        <div style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <p style={{ fontSize: 13, color: C.muted, textAlign: 'center', marginBottom: 16 }}>
            Clique sur chaque forme pour l'entendre
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, marginBottom: 0 }}>
            {(data.formes || []).map((f, i) => (
              <div key={i} onClick={() => { setSelectedForm(i); playAudio(f.forme); }} style={{
                background: selectedForm === i ? C.greenLight : C.white,
                border: `${selectedForm === i ? '2px' : '0.5px'} solid ${selectedForm === i ? C.greenMid : '#e5e7eb'}`,
                borderRadius: 20, padding: '40px 20px',
                textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s',
              }}>
                <div style={{ fontSize: 140, color: C.green, direction: 'rtl', fontFamily: "'Amiri', serif", marginBottom: 8, lineHeight: 1 }}>
                  {f.forme}
                </div>
                <div style={{ fontSize: 16, color: C.muted, fontWeight: 500, marginBottom: 6 }}>{f.position}</div>
                <div style={{ fontSize: 11, color: C.green, direction: 'rtl' }}>{f.position_ar}</div>
                <div style={{ fontSize: 14, color: C.text, direction: 'rtl', fontFamily: "'Amiri', serif", marginTop: 8, borderTop: `0.5px solid #f0f0f0`, paddingTop: 8 }}>
                  {f.exemple}
                </div>
              </div>
            ))}
          </div>
        </div>
      );

      // ── ÉCRAN 4 : Lecture prénoms (Slide 7) ──
      case 4: return (
        <div style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{ fontSize: 18, direction: 'rtl', color: C.text, fontFamily: "'Amiri', serif", marginBottom: 8 }}>
              اقرأ يا أيْمَنُ
            </div>
            <div style={{ fontSize: 13, color: C.muted }}>Lis à voix haute ces prénoms — trouve le ي dans chacun</div>
          </div>
          {(!data.prenoms || data.prenoms.length === 0) && (
            <div style={{
              background: C.greenLight, border: `1px solid ${C.greenBorder}`,
              borderRadius: 12, padding: '24px', textAlign: 'center',
              direction: 'rtl', marginBottom: 16,
            }}>
              <div style={{ fontSize: 24, marginBottom: 8 }}>📖</div>
              <div style={{ fontSize: 18, color: C.green, fontFamily: "'Amiri', serif" }}>
                Le prof va proposer des prénoms à lire
              </div>
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {(data.prenoms || []).map((p, i) => (
              <div key={i} style={{
                background: C.white, border: `0.5px solid #e5e7eb`,
                borderRadius: 16, padding: 24,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <div style={{ textAlign: 'right', flex: 1 }}>
                  <div style={{ fontSize: 110, fontFamily: "'Amiri', serif", direction: 'rtl', marginBottom: 10 }}>
                    <span style={{ color: C.text }}>{p.highlight_avant}</span>
                    <span style={{ color: C.green, fontWeight: 700, fontSize: 56 }}>{p.highlight}</span>
                    <span style={{ color: C.text }}>{p.highlight_apres}</span>
                  </div>
                  <div style={{ fontSize: 14, color: C.muted }}>{p.fr}</div>
                </div>
                <PlayBtn small onClick={() => playAudio(p.ar)} />
              </div>
            ))}
          </div>
          <div style={{
            background: C.greenLight, border: `1px solid ${C.greenBorder}`,
            borderRadius: 10, padding: '12px 16px', marginTop: 16,
            textAlign: 'center', fontSize: 13, color: C.green,
          }}>
            💡 La lettre <strong>ي</strong> est surlignée en vert dans chaque prénom
          </div>
        </div>
      );

      // ── ÉCRAN 5 : Conscience phonique (Slides 8-15) ──
      case 5: return (
        <div style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <Tag>الوعي الصوتي للحرف</Tag>
            <p style={{ fontSize: 13, color: C.muted, marginTop: 8 }}>
              Clique sur chaque mot — écoute et lis le ي en vert
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
            {(data.mots_conscience || []).map((m, i) => (
              <div key={i} style={{
                background: thumbUp === i ? '#f0fff4' : playingWord === m.mot ? C.greenLight : C.white,
                border: `${thumbUp === i || playingWord === m.mot ? '1.5px' : '0.5px'} solid ${thumbUp === i ? C.greenMid : playingWord === m.mot ? C.greenMid : '#e5e7eb'}`,
                borderRadius: 16, padding: '28px 16px',
                textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s',
              }}>
                <div style={{ fontSize: 110, marginBottom: 16 }}>{m.emoji}</div>
                <div style={{ fontSize: 64, direction: 'rtl', fontFamily: "'Amiri', serif", marginBottom: 8 }}>
                  <span style={{ color: C.text }}>{m.highlight_avant}</span>
                  <span style={{ color: C.green, fontWeight: 700 }}>{m.highlight}</span>
                  <span style={{ color: C.text }}>{m.highlight_apres}</span>
                </div>
                <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>{m.fr}</div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                  <button onClick={() => playAudio(m.mot)} style={{
                    padding: '5px 12px', background: C.greenLight, border: 'none',
                    borderRadius: 20, fontSize: 11, color: C.green, cursor: 'pointer', fontWeight: 600,
                  }}>
                    ▶ Écouter
                  </button>
                  <button onClick={() => setThumbUp(thumbUp === i ? null : i)} style={{
                    padding: '5px 12px',
                    background: thumbUp === i ? C.green : '#f9f9f9',
                    border: `1px solid ${thumbUp === i ? C.green : '#e5e7eb'}`,
                    borderRadius: 20, fontSize: 14, cursor: 'pointer',
                  }}>
                    {thumbUp === i ? '👍' : '👍'}
                  </button>
                </div>
                {thumbUp === i && (
                  <div style={{ fontSize: 11, color: C.green, marginTop: 6, fontWeight: 600 }}>
                    ✅ Bien lu !
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      );

      // ── ÉCRAN 6 : Position du son (Slides 16-17) ──
      case 6: return (
        <div style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <Tag>تحديد موقع الصوت</Tag>
            <p style={{ fontSize: 13, color: C.muted, marginTop: 8 }}>
              Écoute le mot — où se trouve le ي ?
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {(data.position_son || []).map((m, i) => (
              <div key={i} style={{
                background: C.white, border: `0.5px solid #e5e7eb`,
                borderRadius: 14, padding: '14px 16px',
                display: 'flex', alignItems: 'center', gap: 14,
              }}>
                <div style={{ fontSize: 64, flexShrink: 0 }}>{m.emoji}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 40, direction: 'rtl', fontFamily: "'Amiri', serif" }}>
                    <span style={{ color: C.text }}>{m.highlight_avant}</span>
                    <span style={{ color: C.green, fontWeight: 700 }}>{m.highlight}</span>
                    <span style={{ color: C.text }}>{m.highlight_apres}</span>
                  </div>
                  <div style={{ fontSize: 12, color: C.muted }}>{m.fr}</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                  <div style={{
                    padding: '4px 12px',
                    background: m.position === 'début' ? '#dbeafe' : m.position === 'milieu' ? C.yellowLight : C.greenLight,
                    color: m.position === 'début' ? C.blue : m.position === 'milieu' ? C.yellow : C.green,
                    borderRadius: 20, fontSize: 11, fontWeight: 600,
                  }}>
                    {m.position === 'début' ? '🔵 Début' : m.position === 'milieu' ? '🟡 Milieu' : '🟢 Fin'}
                  </div>
                  <button onClick={() => playAudio(m.mot)} style={{
                    padding: '4px 10px', background: C.greenLight, border: 'none',
                    borderRadius: 20, fontSize: 11, color: C.green, cursor: 'pointer',
                  }}>▶ Écouter</button>
                </div>
              </div>
            ))}
          </div>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 8, marginTop: 16,
          }}>
            {[
              { label: 'Début', ar: 'أول الكلمة', color: '#dbeafe', textColor: C.blue },
              { label: 'Milieu', ar: 'وسط الكلمة', color: C.yellowLight, textColor: C.yellow },
              { label: 'Fin', ar: 'آخر الكلمة', color: C.greenLight, textColor: C.green },
            ].map((pos, i) => (
              <div key={i} style={{
                background: pos.color, borderRadius: 10, padding: '10px 8px', textAlign: 'center',
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: pos.textColor }}>{pos.label}</div>
                <div style={{ fontSize: 11, direction: 'rtl', color: pos.textColor, marginTop: 2 }}>{pos.ar}</div>
              </div>
            ))}
          </div>
        </div>
      );

      // ── ÉCRAN 7 : Voyelles (Slides 18-20) ──
      case 7: return (
        <div style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 12, color: C.muted, fontWeight: 600, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              الحركات القصيرة — Voyelles courtes
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
              {(data.harakat_courtes || []).slice(0, 4).map((h, i) => (
                <div key={i} onClick={() => playAudio(h.syllabe)} style={{
                  background: C.white, border: '0.5px solid #e5e7eb',
                  borderRadius: 14, padding: '28px 14px', textAlign: 'center', cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
                  onMouseEnter={e => e.currentTarget.style.background = C.greenLight}
                  onMouseLeave={e => e.currentTarget.style.background = C.white}
                >
                  <div style={{ fontSize: 96, color: C.green, fontFamily: "'Amiri', serif", direction: 'rtl', lineHeight: 1.2 }}>{h.syllabe}</div>
                  <div style={{ fontSize: 10, color: C.muted, marginTop: 6 }}>{h.nom_fr}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 12, color: C.muted, fontWeight: 600, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              التنوين — Tanwin
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              {(data.harakat_courtes || []).slice(4).map((h, i) => (
                <div key={i} onClick={() => playAudio(h.syllabe)} style={{
                  background: C.white, border: '0.5px solid #e5e7eb',
                  borderRadius: 12, padding: '14px 6px', textAlign: 'center', cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
                  onMouseEnter={e => e.currentTarget.style.background = C.greenLight}
                  onMouseLeave={e => e.currentTarget.style.background = C.white}
                >
                  <div style={{ fontSize: 44, color: C.green, fontFamily: "'Amiri', serif", direction: 'rtl' }}>{h.syllabe}</div>
                  <div style={{ fontSize: 9, color: C.muted, marginTop: 4 }}>{h.nom_fr}</div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: C.muted, fontWeight: 600, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              الحركات الطويلة — Voyelles longues
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              {(data.harakat_longues || []).map((h, i) => (
                <div key={i} onClick={() => playAudio(h.syllabe)} style={{
                  background: C.white, border: '0.5px solid #e5e7eb',
                  borderRadius: 12, padding: '14px 6px', textAlign: 'center', cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
                  onMouseEnter={e => e.currentTarget.style.background = C.greenLight}
                  onMouseLeave={e => e.currentTarget.style.background = C.white}
                >
                  <div style={{ fontSize: 44, color: C.green, fontFamily: "'Amiri', serif", direction: 'rtl' }}>{h.syllabe}</div>
                  <div style={{ fontSize: 9, color: C.muted, marginTop: 4 }}>{h.nom_fr}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      );

      // ── ÉCRAN 8 : Décomposition syllabique (Slide 21) ──
      case 8: return (
        <div style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <Tag>التجزيء المقطعي</Tag>
            <p style={{ fontSize: 13, color: C.muted, marginTop: 8 }}>
              Décomposition en syllabes
            </p>
          </div>

          {/* Tableau décomposition — avec fallback si pas de données */}
          {(!data.decomposition || data.decomposition.length === 0) ? (
            <div style={{
              background: C.greenLight, border: `1px solid ${C.greenBorder}`,
              borderRadius: 12, padding: '24px', textAlign: 'center',
              direction: 'rtl', marginBottom: 16,
            }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📚</div>
              <div style={{ fontSize: 18, color: C.green, fontFamily: "'Amiri', serif", marginBottom: 8 }}>
                التجزيء المقطعي
              </div>
              <div style={{ fontSize: 14, color: C.muted }}>
                Le prof va faire la décomposition syllabique à l'oral
              </div>
            </div>
          ) : (
          <div style={{
            background: C.white, border: '0.5px solid #e5e7eb',
            borderRadius: 14, overflow: 'hidden', marginBottom: 16,
          }}>
            {/* En-tête tableau */}
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 2fr 80px',
              background: C.green, color: '#fff', padding: '10px 16px',
              fontSize: 12, fontWeight: 700, direction: 'rtl',
            }}>
              <div>الكلمات</div>
              <div>التجزيء المقطعي</div>
              <div style={{ textAlign: 'center' }}>عدد المقاطع</div>
            </div>
            {/* Lignes tableau */}
            {(data.decomposition || []).map((d, i) => (
              <div key={i} style={{
                display: 'grid', gridTemplateColumns: '1fr 2fr 80px',
                padding: '14px 16px', direction: 'rtl',
                borderBottom: i < data.decomposition.length - 1 ? '0.5px solid #f0f0f0' : 'none',
                background: i % 2 === 0 ? C.bg : C.white,
              }}>
                <div>
                  <div style={{ fontSize: 40, fontFamily: "'Amiri', serif", color: C.text }}>{d.mot}</div>
                  <div style={{ fontSize: 11, color: C.muted }}>{d.fr}</div>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', direction: 'rtl' }}>
                  {(d.syllabes || []).map((s, j) => (
                    <React.Fragment key={j}>
                      <span style={{
                        background: [C.greenLight, C.yellowLight, C.blueLight][j % 3],
                        color: [C.green, C.yellow, C.blue][j % 3],
                        padding: '6px 14px', borderRadius: 20, fontSize: 32,
                        fontFamily: "'Amiri', serif", fontWeight: 600,
                        cursor: 'pointer',
                      }} onClick={() => playAudio(s)}>{s}</span>
                      {j < d.syllabes.length - 1 && (
                        <span style={{ color: C.muted, fontSize: 12 }}>—</span>
                      )}
                    </React.Fragment>
                  ))}
                </div>
                <div style={{ textAlign: 'center' }}>
                  <span style={{
                    width: 32, height: 32, borderRadius: '50%',
                    background: C.green, color: '#fff',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 14, fontWeight: 700,
                  }}>{d.nb}</span>
                </div>
              </div>
            ))}
          </div>
          )}

          <div style={{
            background: C.greenLight, border: `1px solid ${C.greenBorder}`,
            borderRadius: 10, padding: '12px 16px', direction: 'rtl',
            fontSize: 14, color: C.green, fontFamily: "'Amiri', serif",
          }}>
            {data.decomposition_consigne}
          </div>
        </div>
      );

      // ── ÉCRAN 9 : Fluidité (Slide 22) ──
      case 9: return (
        <div style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <Tag>الطلاقة</Tag>
          </div>

          {/* Toggle niveaux */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, justifyContent: 'center' }}>
            {[
              { n: 1, label: 'Syllabes' },
              { n: 2, label: 'Mots' },
              { n: 3, label: 'Phrases' },
            ].map(({ n, label }) => (
              <button key={n} onClick={() => setFluiditeLevel(n)} style={{
                padding: '7px 16px', borderRadius: 20, border: 'none',
                background: fluiditeLevel === n ? C.green : '#f0f0f0',
                color: fluiditeLevel === n ? '#fff' : C.muted,
                fontWeight: 600, fontSize: 12, cursor: 'pointer',
                transition: 'all 0.2s',
              }}>{label}</button>
            ))}
          </div>

          {/* Niveau 1 — Syllabes */}
          {fluiditeLevel === 1 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center', direction: 'rtl' }}>
              {(data.fluidite?.niveau1_syllabes || []).length === 0 && (
                <div style={{ color: C.muted, fontSize: 14, padding: 20 }}>
                  Le prof va lire les syllabes à voix haute
                </div>
              )}
              {(data.fluidite?.niveau1_syllabes || []).map((s, i) => (
                <div key={i} onClick={() => playAudio(s)} style={{
                  background: C.white, border: '0.5px solid #e5e7eb',
                  borderRadius: 12, padding: '14px 18px', cursor: 'pointer',
                  fontSize: 72, color: C.green, fontFamily: "'Amiri', serif",
                  transition: 'all 0.15s', textAlign: 'center',
                }}
                  onMouseEnter={e => { e.currentTarget.style.background = C.greenLight; e.currentTarget.style.borderColor = C.greenMid; }}
                  onMouseLeave={e => { e.currentTarget.style.background = C.white; e.currentTarget.style.borderColor = '#e5e7eb'; }}
                >
                  {s}
                </div>
              ))}
            </div>
          )}

          {/* Niveau 2 — Mots */}
          {fluiditeLevel === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(data.fluidite?.niveau2_mots || []).length === 0 && (
                <div style={{ color: C.muted, fontSize: 14, padding: 20, textAlign: 'center' }}>
                  Le prof va lire les mots à voix haute
                </div>
              )}
              {(data.fluidite?.niveau2_mots || []).map((m, i) => (
                <div key={i} onClick={() => playAudio(m)} style={{
                  background: C.white, border: '0.5px solid #e5e7eb',
                  borderRadius: 12, padding: '12px 20px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  transition: 'all 0.15s',
                }}
                  onMouseEnter={e => e.currentTarget.style.background = C.greenLight}
                  onMouseLeave={e => e.currentTarget.style.background = C.white}
                >
                  <div style={{ fontSize: 64, direction: 'rtl', fontFamily: "'Amiri', serif", color: C.text }}>{m}</div>
                  <span style={{ fontSize: 11, color: C.green }}>▶</span>
                </div>
              ))}
            </div>
          )}

          {/* Niveau 3 — Phrases */}
          {fluiditeLevel === 3 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {(data.fluidite?.niveau3_phrases || []).length === 0 && (
                <div style={{ color: C.muted, fontSize: 14, padding: 20, textAlign: 'center' }}>
                  Le prof va lire les phrases à voix haute
                </div>
              )}
              {(data.fluidite?.niveau3_phrases || []).map((p, i) => (
                <div key={i} onClick={() => playAudio(p.ar)} style={{
                  background: C.white, border: '0.5px solid #e5e7eb',
                  borderRadius: 14, padding: '16px 20px', cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                  onMouseEnter={e => e.currentTarget.style.background = C.greenLight}
                  onMouseLeave={e => e.currentTarget.style.background = C.white}
                >
                  <div style={{ fontSize: 11, color: C.greenMid, marginBottom: 6, fontWeight: 600 }}>0{i + 1}</div>
                  <div style={{ fontSize: 56, direction: 'rtl', fontFamily: "'Amiri', serif", color: C.text, lineHeight: 1.8, marginBottom: 8 }}>
                    {p.ar}
                  </div>
                  <div style={{ fontSize: 13, color: C.muted, fontStyle: 'italic' }}>{p.fr}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      );

      // ── ÉCRAN 10 : Écriture (Slide 23) ──
      case 10: return (
        <div style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <Tag>أتدرب وأكتب</Tag>
          </div>

          {/* Partie 1 — Tracé animé SVG */}
          <LetterTracingAnimation letter={data.lettre} />

          {/* Séparateur */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, margin: '16px 0',
          }}>
            <div style={{ flex: 1, height: 1, background: '#f0f0f0' }} />
            <span style={{ fontSize: 12, color: C.muted, whiteSpace: 'nowrap' }}>
              ✏️ Zone d'écriture partagée
            </span>
            <div style={{ flex: 1, height: 1, background: '#f0f0f0' }} />
          </div>

          {/* Partie 2 — Canvas partagé */}
          <EcritureCanvas
            client={client}
            userType={userType}
            letter={data.lettre}
            lignes={data.ecriture}
          />
        </div>
      );

      default: return null;
    }
  };

  // ─── ÉCRAN RÉSULTAT FINAL ──────────────────
  if (screen >= TOTAL) {
    return (
      <div style={styles.overlay}>
        <div style={{ ...styles.box, textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: 64, marginBottom: 12 }}>🌟</div>
          <div style={{ fontSize: 26, color: C.text, fontWeight: 700, marginBottom: 4 }}>Bravo !</div>
          <div style={{ fontSize: 120, color: C.green, fontFamily: "'Amiri', serif", lineHeight: 1, marginBottom: 16 }}>
            {data.lettre}
          </div>
          <div style={{ fontSize: 15, color: C.muted, marginBottom: 24 }}>
            Cours complet sur la lettre <strong>{data.nom}</strong> terminé
          </div>
          <div style={{
            background: C.greenLight, border: `1px solid ${C.greenBorder}`,
            borderRadius: 12, padding: '16px 20px', marginBottom: 24, textAlign: 'left',
          }}>
            {[
              '✓ Objectifs pédagogiques vus',
              '✓ Mise en situation',
              '✓ Lettre et articulation',
              '✓ Les 4 formes',
              '✓ Lecture de prénoms',
              '✓ Conscience phonique (4 mots)',
              '✓ Position du son',
              '✓ Voyelles et tanwin',
              '✓ Décomposition syllabique',
              '✓ Fluidité (3 niveaux)',
              '✓ Écriture (4 lignes)',
            ].map((item, i) => (
              <div key={i} style={{ fontSize: 13, color: C.green, marginBottom: 4 }}>{item}</div>
            ))}
          </div>
          <button onClick={onClose} style={{
            padding: '12px 32px', background: C.green, color: '#fff',
            border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer',
          }}>
            Fermer ✓
          </button>
        </div>
      </div>
    );
  }

  // ─── RENDU PRINCIPAL ──────────────────────
  return (
    <div style={styles.overlay}>
      <div style={styles.box}>

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 24px', paddingBottom: 12, borderBottom: '0.5px solid #e5e7eb', flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 52, height: 52, borderRadius: '50%',
              background: `linear-gradient(135deg, ${C.green}, ${C.greenMid})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 28, color: '#fff', fontFamily: "'Amiri', serif",
              direction: 'rtl', flexShrink: 0,
            }}>
              {data.lettre}
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{data.nom}</div>
              <div style={{ fontSize: 11, color: C.muted }}>{data.transliteration} · Niveau Abjad</div>
            </div>
          </div>
          <button onClick={onClose} style={{
            width: 32, height: 32, borderRadius: '50%',
            border: '0.5px solid #e5e7eb', background: '#f9f9f9',
            cursor: 'pointer', fontSize: 16, color: C.muted,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>✕</button>
        </div>

        {/* Barre progression */}
        <div style={{ marginBottom: 8, padding: '0 24px', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{screenTitles[screen]}</span>
            <span style={{ fontSize: 11, color: C.muted }}>{screen + 1} / {TOTAL}</span>
          </div>
          <div style={{ background: '#f0f0f0', borderRadius: 20, height: 5, overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              width: `${((screen + 1) / TOTAL) * 100}%`,
              background: `linear-gradient(90deg, ${C.green}, ${C.greenMid})`,
              borderRadius: 20, transition: 'width 0.4s ease',
            }} />
          </div>
          <div style={{ display: 'flex', gap: 4, marginTop: 8, justifyContent: 'center' }}>
            {Array.from({ length: TOTAL }).map((_, i) => (
              <div key={i} style={{
                width: i === screen ? 18 : 6, height: 6, borderRadius: 3,
                background: i < screen ? C.greenMid : i === screen ? C.green : '#e5e7eb',
                transition: 'all 0.3s',
              }} />
            ))}
          </div>
        </div>

        {/* Contenu */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px 8px' }}>
          {renderScreen()}
        </div>

        {/* Navigation */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '12px 24px', borderTop: '0.5px solid #e5e7eb', flexShrink: 0,
        }}>
          <button onClick={() => navigate(-1)} disabled={screen === 0} style={{
            padding: '10px 20px', borderRadius: 10,
            border: '0.5px solid #e5e7eb',
            background: screen === 0 ? '#f9f9f9' : C.white,
            color: screen === 0 ? '#ccc' : C.text,
            fontSize: 14, fontWeight: 600,
            cursor: screen === 0 ? 'not-allowed' : 'pointer',
          }}>
            ← Retour
          </button>
          <div style={{ fontSize: 24, color: C.green, fontFamily: "'Amiri', serif" }}>
            {data.lettre}
          </div>
          <button onClick={() => screen === TOTAL - 1 ? setScreen(TOTAL) : navigate(1)} style={{
            padding: '10px 20px', borderRadius: 10, border: 'none',
            background: `linear-gradient(135deg, ${C.green}, ${C.greenMid})`,
            color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
          }}>
            {isSlaveMode ? '👁️ Mode élève' : screen === TOTAL - 1 ? 'Terminer ✓' : 'Suivant →'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────
const styles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 74,
    background: '#f8f7f4', zIndex: 5000,
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden',
  },
  box: {
    background: '#fff', borderRadius: 20, padding: 16,
    flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden',
  },
};

export default LettreInteractive;
